<!-- Patient_CareTeam.vue -->
<template>
  <div class="space-y-5">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <h4 class="text-sm font-semibold text-gray-700">Care Team</h4>
      <button
        class="text-[11px] text-gray-400 hover:text-gray-600"
        :title="showHelp ? 'Hide explanation' : 'How does this work?'"
        @click="showHelp = !showHelp"
      >
        <FontAwesomeIcon :icon="['fas', 'circle-question']" class="mr-1" />
        {{ showHelp ? 'Hide' : 'How this works' }}
      </button>
    </div>

    <!-- Explainer. Open on first visit (persisted), because nothing else on screen
         tells you that reassigning one task doesn't move the rest of the case.
         Copy is Nick's, verbatim — do not "improve" it. -->
    <div v-if="showHelp" class="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 text-xs text-gray-700 space-y-2">
      <p>
        <span class="font-semibold text-emerald-700">Case Owner:</span> The default
        owner for all tasks moving forward for this opportunity. Even if you reassign
        an individual task, unless you reassign task ownership, the next task will
        return to the task owner unless superseded by a task-specific rule.
      </p>
      <p>
        <span class="font-semibold text-blue-700">Assigned:</span> The current stage
        owner for each open stage
      </p>
      <p>
        <span class="font-semibold text-amber-700">Coverage Rules:</span> The set of
        rules that govern which pool of team members are assigned ownership and/or
        individual stage ownership. A stage-specific rule will override an
        &ldquo;All-tasks&rdquo; rule.
      </p>
    </div>

    <!-- Loading Skeleton -->
    <div v-if="loading" class="space-y-5">
      <div v-for="n in 3" :key="'sk-section-' + n">
        <div class="h-3 w-20 bg-gray-200 rounded animate-pulse mb-2"></div>
        <div class="space-y-2">
          <div v-for="c in (n === 1 ? 1 : 2)" :key="'sk-card-' + n + '-' + c" class="bg-white border border-gray-200 rounded-lg px-4 py-3">
            <div class="flex items-center space-x-3">
              <div class="w-8 h-8 rounded-full bg-gray-200 animate-pulse shrink-0"></div>
              <div class="flex-1 space-y-1.5">
                <div class="h-3.5 w-28 bg-gray-200 rounded animate-pulse"></div>
                <div class="h-3 w-16 bg-gray-100 rounded animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template v-else>
      <!-- Section 1: Case Owner -->
      <!-- ONE owner per program. This is the record the next task returns to, and
           it is deliberately NOT stage-scoped: which stages the owner can work is
           decided by the coverage rules, not by the ownership row. Shown because
           without it "Darlene is on the intake" reads as ownership when it isn't. -->
      <div v-if="caseOwners.length">
        <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Case Owner</p>
        <div class="space-y-2">
          <div
            v-for="o in caseOwners"
            :key="'co-' + o.opportunity_id"
            class="bg-white border border-gray-200 rounded-lg px-4 py-3"
          >
            <div class="flex items-center justify-between gap-2 mb-1">
              <span class="text-[11px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 font-medium">
                {{ o.metric_name }}
              </span>
              <button
                class="shrink-0 text-[11px] px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50"
                @click="toggleOwnerForm(o)"
              >
                <FontAwesomeIcon :icon="['fas', o.member_id ? 'right-left' : 'user-plus']" class="mr-1" />
                {{ o.member_id ? 'Reassign owner' : 'Set owner' }}
              </button>
            </div>

            <div v-if="o.member_id" class="flex items-center space-x-3">
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                :class="roleColor(o.role_name).bg"
              >
                <FontAwesomeIcon
:icon="['fas', roleIcon(o.role_name)]"
                  :class="roleColor(o.role_name).text" class="text-xs" />
              </div>
              <div class="min-w-0 flex-1">
                <p class="sensitive font-medium text-sm text-gray-900">{{ o.member_name }}</p>
                <p class="text-xs text-gray-500 mt-0.5">{{ o.role_name || 'No role' }}</p>
              </div>
              <button
                :disabled="savingOwner"
                class="shrink-0 text-[11px] px-2 py-1 text-gray-400 hover:text-red-600 disabled:opacity-50"
                :title="'Remove ' + o.member_name + ' as owner'"
                @click="clearOwner(o)"
              >
                <FontAwesomeIcon :icon="['fas', 'xmark']" />
              </button>
            </div>
            <p v-else class="text-xs text-gray-400">
              No owner set. Tasks go to whoever covers this practice.
            </p>

            <div v-if="ownerFormOppId === o.opportunity_id" class="mt-3 pt-3 border-t border-gray-100 space-y-2">
              <SimpleMultiSelect
                v-model="ownerTo"
                :options="memberOptions"
                placeholder="Reassign owner to…"
                :searchable="true"
                :multiple="false"
              />
              <div class="flex items-center gap-2">
                <button
                  :disabled="!ownerTo.length || savingOwner"
                  class="text-xs px-3 py-1.5 rounded font-medium text-white disabled:opacity-50"
                  style="background-color: #F76734;"
                  @click="transferOwner(o)"
                >{{ savingOwner ? 'Saving…' : 'Confirm' }}</button>
                <button class="text-xs px-2 py-1.5 text-gray-500 hover:text-gray-700" @click="ownerFormOppId = null">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Section 2: Task Assignments -->
      <div>
        <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Assigned</p>
        <div v-if="taskAssignments.length" class="space-y-2">
          <div
            v-for="a in taskAssignments"
            :key="'ta-' + a.task_id"
            class="bg-white border border-gray-200 rounded-lg px-4 py-3"
          >
            <div class="flex items-center space-x-3">
              <div
class="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                :class="roleColor(a.role_name).bg"
              >
                <FontAwesomeIcon
:icon="['fas', roleIcon(a.role_name)]"
                  :class="roleColor(a.role_name).text" class="text-xs" />
              </div>
              <div class="min-w-0 flex-1">
                <p class="sensitive font-medium text-sm" :class="a.member_id ? 'text-gray-900' : 'text-gray-400 italic'">{{ a.member_name || 'Unassigned' }}</p>
                <p class="text-xs text-gray-500 mt-0.5">{{ a.role_name || 'No role' }}</p>
                <div class="flex items-center gap-1.5 mt-1 flex-wrap">
                  <span
v-if="a.metric_name"
                    class="text-[11px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-700">
                    {{ a.metric_name }}
                  </span>
                  <span
v-if="a.task_type_name"
                    class="text-[11px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">
                    {{ a.task_type_name }}
                  </span>
                </div>
              </div>
              <button
                class="shrink-0 text-[11px] px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50"
                @click="reassignTaskId = reassignTaskId === a.task_id ? null : a.task_id"
              >
                <FontAwesomeIcon :icon="['fas', a.member_id ? 'right-left' : 'user-plus']" class="mr-1" />
                {{ a.member_id ? 'Reassign' : 'Assign' }}
              </button>
            </div>

            <!-- Inline assign/reassign: sets the ASSIGNEE on THIS TASK ONLY.
                 It does NOT transfer case ownership — use the Case Owner section
                 above for that. (This comment used to say "sets the owner", which
                 is what made the reverting-stage behaviour so confusing.) -->
            <div v-if="reassignTaskId === a.task_id" class="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2">
              <SimpleMultiSelect
                v-model="reassignTo"
                :options="memberOptions"
                :placeholder="a.member_id ? 'Reassign to…' : 'Assign to…'"
                :searchable="true"
                :multiple="false"
                class="flex-1"
              />
              <button
                :disabled="!reassignTo.length || reassigningTask"
                class="text-xs px-3 py-1.5 rounded font-medium text-white disabled:opacity-50"
                style="background-color: #F76734;"
                @click="reassignTask(a.task_id)"
              >{{ reassigningTask ? 'Saving…' : 'Confirm' }}</button>
              <button class="text-xs px-2 py-1.5 text-gray-500 hover:text-gray-700" @click="reassignTaskId = null">Cancel</button>
            </div>
          </div>
        </div>
        <p v-else class="text-xs text-gray-400">No task assignments</p>
      </div>

      <!-- Section 3: Coverage Rules -->
      <div>
        <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Coverage Rules</p>
        <div v-if="coverageRules.length" class="space-y-2">
          <div
            v-for="r in coverageRules"
            :key="'cr-' + r.id"
            class="bg-gray-50 border border-gray-100 rounded-lg px-4 py-2.5"
          >
            <div class="flex items-center space-x-3">
              <div class="w-7 h-7 rounded-full flex items-center justify-center shrink-0 bg-gray-200">
                <FontAwesomeIcon
:icon="['fas', roleIcon(r.role_name)]"
                  class="text-gray-500 text-xs" />
              </div>
              <div class="min-w-0">
                <p class="sensitive font-medium text-xs text-gray-700">{{ r.member_name }}</p>
                <div class="flex items-center gap-1.5 mt-1 flex-wrap">
                  <!-- A rule naming a STAGE overrides the case owner for that stage;
                       a rule with no stage is an ownership rule (the pool a case
                       owner is picked from). They look identical without this. -->
                  <span
                    class="text-[11px] px-1.5 py-0.5 rounded font-medium"
                    :class="r.task_type_id
                      ? 'bg-amber-50 text-amber-700'
                      : 'bg-emerald-50 text-emerald-700'"
                  >{{ r.task_type_id ? r.task_type_name : 'All tasks' }}</span>
                  <span
v-if="r.role_name"
                    class="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                    {{ r.role_name }}
                  </span>
                  <span
v-if="r.metric_name"
                    class="text-[11px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-700">
                    {{ r.metric_name }}
                  </span>
                  <span
v-if="r.entity_name"
                    class="sensitive text-[11px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">
                    {{ r.entity_type_name ? r.entity_type_name + ': ' : '' }}{{ r.entity_name }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <p v-else class="text-xs text-gray-400">No coverage rules match this patient</p>
      </div>

      <!-- Section 4: Assignment History -->
      <div v-if="historyGroups.length">
        <button
          class="w-full flex items-center justify-between text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2"
          @click="showHistory = !showHistory"
        >
          <span>Assignment History</span>
          <FontAwesomeIcon :icon="['fas', showHistory ? 'chevron-up' : 'chevron-down']" class="text-gray-400" />
        </button>
        <div v-if="showHistory" class="space-y-3">
          <div
            v-for="g in historyGroups"
            :key="'hg-' + g.task_id"
            class="bg-gray-50 border border-gray-100 rounded-lg px-4 py-3"
          >
            <p class="text-[11px] font-medium text-gray-600 mb-2">
              <span v-if="g.metric_name" class="text-violet-700">{{ g.metric_name }}</span>
              <span v-if="g.task_type_name" class="text-gray-500"> · {{ g.task_type_name }}</span>
            </p>
            <ol class="relative border-l border-gray-200 ml-1.5 space-y-3">
              <li
                v-for="(ev, i) in g.events"
                :key="'he-' + g.task_id + '-' + i"
                class="ml-3"
              >
                <span
                  class="absolute -left-[5px] w-2.5 h-2.5 rounded-full"
                  :class="ev.is_current ? 'bg-emerald-500' : 'bg-gray-300'"
                ></span>
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="sensitive text-xs font-medium text-gray-900">{{ ev.member_name || '—' }}</span>
                  <span v-if="ev.role_name" class="text-[11px] text-gray-500">{{ ev.role_name }}</span>
                  <span
                    v-if="ev.is_current"
                    class="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-medium"
                  >Current</span>
                </div>
                <p class="text-[11px] text-gray-500 mt-0.5">
                  {{ fmtDate(ev.assigned_at) }}<span v-if="ev.ended_at"> – {{ fmtDate(ev.ended_at) }}</span>
                  <span v-if="ev.assigned_by" class="sensitive"> · by {{ ev.assigned_by }}</span>
                </p>
              </li>
            </ol>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!taskAssignments.length && !coverageRules.length" class="text-center py-6">
        <FontAwesomeIcon :icon="['fas', 'users']" class="text-3xl text-gray-300 mb-2" />
        <p class="text-gray-500 text-sm">No care team members assigned</p>
      </div>
    </template>
  </div>
</template>

<script>
import { z } from 'zod';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import SimpleMultiSelect from '@/components/widgetcomponents/Dropdown_Selector/SimpleMultiSelect.vue';
import { BrowserStore } from '@/utils/BrowserStore';
import abortable from '@/mixins/abortable';

// Explainer starts OPEN (default true) and stays closed once dismissed. The
// owner-vs-assignee distinction is not guessable from the UI, so a first-visit
// nudge is worth it; nagging afterwards is not.
const helpStore = new BrowserStore(
  'care_team_help_visible',
  z.object({ showHelp: z.boolean().default(true) }),
);

const ROLE_STYLES = {
  'RN':             { bg: 'bg-rose-100',    text: 'text-rose-600',    icon: 'user-nurse' },
  'Health Coach':   { bg: 'bg-green-100',   text: 'text-green-600',   icon: 'heart-pulse' },
  'PharmD':         { bg: 'bg-purple-100',  text: 'text-purple-600',  icon: 'pills' },
  'LCSW':           { bg: 'bg-amber-100',   text: 'text-amber-600',   icon: 'hand-holding-heart' },
  'MA':             { bg: 'bg-cyan-100',    text: 'text-cyan-600',    icon: 'stethoscope' },
  'Primary Owner':  { bg: 'bg-primary-100', text: 'text-primary-600', icon: 'star' },
  'Supervisor':     { bg: 'bg-indigo-100',  text: 'text-indigo-600',  icon: 'shield' },
  'Consultant':     { bg: 'bg-gray-100',    text: 'text-gray-600',    icon: 'comment-medical' },
};

const DEFAULT_STYLE = { bg: 'bg-gray-100', text: 'text-gray-500', icon: 'user' };

export default {
  name: 'PatientCareTeam',
  components: { FontAwesomeIcon, SimpleMultiSelect },
  mixins: [abortable],

  props: {
    patientIdentifier: { type: String, required: true },
    entityId: { type: [String, Number], default: null },
    opportunityId: { type: [String, Number], default: null },
  },

  emits: ['show-message'],

  data() {
    return {
      showHelp: helpStore.load().showHelp,
      caseOwners: [],
      taskAssignments: [],
      coverageRules: [],
      historyEvents: [],
      showHistory: false,
      memberEntities: [],
      loading: true,
      reassignTaskId: null,
      reassignTo: [],
      reassigningTask: false,
      ownerFormOppId: null,
      ownerTo: [],
      savingOwner: false,
    };
  },

  computed: {
    memberOptions() {
      return this.memberEntities.map(e => ({ value: e.id, label: e.name }));
    },

    // patient-history/ returns a FLAT events[] (one row per ownership interval,
    // each carrying task_id/task_type_name/metric_name). The timeline renders
    // one card per TASK, so group here rather than asking the API to nest —
    // insertion order is preserved, so the endpoint's sort still governs display.
    historyGroups() {
      const groups = [];
      const byTask = new Map();
      for (const ev of this.historyEvents || []) {
        let g = byTask.get(ev.task_id);
        if (!g) {
          g = {
            task_id: ev.task_id,
            task_type_name: ev.task_type_name,
            metric_name: ev.metric_name,
            events: [],
          };
          byTask.set(ev.task_id, g);
          groups.push(g);
        }
        g.events.push(ev);
      }
      return groups;
    },
  },

  watch: {
    patientIdentifier() {
      const signal = this._renewAbortSignal();
      this.loadCareTeam(signal);
      this.loadHistory(signal);
    },
    showHelp(v) {
      helpStore.save({ showHelp: v });
    },
  },

  mounted() {
    this.loadOptions();
    this.loadCareTeam();
    this.loadHistory();
  },

  methods: {
    roleColor(roleName) {
      return ROLE_STYLES[roleName] || DEFAULT_STYLE;
    },

    roleIcon(roleName) {
      return (ROLE_STYLES[roleName] || DEFAULT_STYLE).icon;
    },

    fmtDate(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      if (isNaN(d)) return iso;
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },

    async loadHistory(signal) {
      if (!this.patientIdentifier && !this.entityId) return;
      try {
        const params = {};
        if (this.patientIdentifier) params.patient_identifier = this.patientIdentifier;
        if (this.entityId) params.entity_id = this.entityId;
        const { data } = await this.$axios.get('/api/team-assignments/patient-history/', { params, signal });
        this.historyEvents = data.events || [];
      } catch (error) {
        if (this._isAborted(error)) return;
        console.error('Error loading assignment history:', error);
        this.historyEvents = [];
      }
    },

    async loadOptions() {
      // Member list for the per-task Reassign control (CM Staff + CM Manager).
      try {
        const memberRes = await Promise.all([
          this.$axios.get('/api/entities/', { params: { entity_type_name: 'CM Staff', page_size: 500 } }),
          this.$axios.get('/api/entities/', { params: { entity_type_name: 'CM Manager', page_size: 500 } }),
        ]);
        const all = [];
        for (const res of memberRes) {
          all.push(...(res.data.results || res.data || []));
        }
        this.memberEntities = all.sort((a, b) => a.name.localeCompare(b.name));
      } catch (error) {
        console.error('Error loading members:', error);
      }
    },

    async loadCareTeam(signal) {
      if (!this.patientIdentifier && !this.entityId) {
        this.loading = false;
        return;
      }
      this.loading = true;
      try {
        const params = {};
        if (this.patientIdentifier) params.patient_identifier = this.patientIdentifier;
        if (this.entityId) params.entity_id = this.entityId;

        const { data } = await this.$axios.get('/api/team-assignments/resolve-patient/', { params, signal });
        this.caseOwners = data.case_owners || [];
        this.taskAssignments = data.task_assignments || [];
        this.coverageRules = data.coverage_rules || [];
      } catch (error) {
        if (this._isAborted(error)) return;
        console.error('Error loading care team:', error);
        this.caseOwners = [];
        this.taskAssignments = [];
        this.coverageRules = [];
      } finally {
        if (!signal?.aborted) this.loading = false;
      }
    },

    toggleOwnerForm(owner) {
      const opening = this.ownerFormOppId !== owner.opportunity_id;
      this.ownerFormOppId = opening ? owner.opportunity_id : null;
      this.ownerTo = [];
    },

    // Transfer durable ownership. Sends opportunity_ids (the OWNER grain), NOT
    // task_ids — task_ids would only change who is on the current task and is
    // exactly the confusion this section exists to remove.
    async transferOwner(owner) {
      if (!this.ownerTo.length) return;
      this.savingOwner = true;
      try {
        await this.$axios.post('/api/team-assignments/bulk-assign/', {
          member_id: this.ownerTo[0],
          opportunity_ids: [owner.opportunity_id],
        });
        this.ownerFormOppId = null;
        this.ownerTo = [];
        await this.loadCareTeam();
        this.$emit('show-message', { text: 'Case ownership transferred', type: 'success' });
      } catch (error) {
        console.error('Error transferring case owner:', error);
        this.$emit('show-message', { text: 'Failed to transfer ownership', type: 'error' });
      } finally {
        this.savingOwner = false;
      }
    },

    // Remove the owner. Tasks then fall back to coverage-rule round-robin, which
    // is the correct no-owner behaviour — not an error state.
    async clearOwner(owner) {
      if (!owner.member_id) return;
      this.savingOwner = true;
      try {
        await this.$axios.post('/api/team-assignments/bulk-unassign/', {
          member_id: owner.member_id,
          opportunity_ids: [owner.opportunity_id],
        });
        this.ownerFormOppId = null;
        await this.loadCareTeam();
        this.$emit('show-message', { text: 'Case owner cleared', type: 'success' });
      } catch (error) {
        console.error('Error clearing case owner:', error);
        this.$emit('show-message', { text: 'Failed to clear owner', type: 'error' });
      } finally {
        this.savingOwner = false;
      }
    },

    async reassignTask(taskId) {
      if (!this.reassignTo.length) return;
      this.reassigningTask = true;
      try {
        // replace:true removes the existing owner(s) on this task and sets the new one.
        await this.$axios.post('/api/team-assignments/bulk-assign/', {
          member_id: this.reassignTo[0],
          task_ids: [taskId],
          replace: true,
        });
        this.reassignTaskId = null;
        this.reassignTo = [];
        await this.loadCareTeam();
        await this.loadHistory();
      } catch (error) {
        console.error('Error reassigning task:', error);
        this.$emit('show-message', 'Failed to reassign', 'error');
      } finally {
        this.reassigningTask = false;
      }
    },

  }
};
</script>
